import { computeStepAndRepeatNumbering, fromVerifyDifference } from '@verify/shared-utils';
import type { Difference } from 'store/request/differences/types';
import type { CtfMatchResultWithDifferences } from 'store/request/match/types';
import type { InspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import type { SortDifferencesOption } from 'types';

// Types and enums for the step-and-repeat difference list. Kept here (not in inspectionSelectors)
// so the pure list builder can be imported and unit-tested without pulling in the pdftron graph.
export enum ListItemType {
  Subheading = 'subheading',
  Difference = 'difference',
  NoDifferences = 'no-differences',
  CollapsibleHeader = 'collapsible-header',
}

export type ListItem =
  | {
      type: ListItemType.Subheading;
      repeat: CtfMatchResultWithDifferences & { differencesPostFiltering: Difference[] };
      sourcePageNumber: number;
    }
  | { type: ListItemType.Difference; difference: Difference; index: number }
  | {
      type: ListItemType.NoDifferences;
      repeat: CtfMatchResultWithDifferences & { differencesPostFiltering: Difference[] };
    }
  | { type: ListItemType.CollapsibleHeader; count: number };

export type RepeatWithPage = {
  repeat: CtfMatchResultWithDifferences & { differencesPostFiltering: Difference[] };
  sourcePageNumber: number;
};

/**
 * Builds the step-and-repeat list items (subheadings + differences + the no-differences section).
 *
 * Difference order and numbering come from the shared computeStepAndRepeatNumbering so the UI and
 * the report stay in sync: repeats stay in (page, matchIndex) order, differences within a repeat
 * follow the active sort, and each difference's sequential number is stored 0-based on `index`.
 */
export const buildStepAndRepeatListItems = (
  repeatsWithDifferences: RepeatWithPage[],
  repeatsWithoutDifferences: RepeatWithPage[],
  sortOption: SortDifferencesOption,
  inspectionCustomStatuses: InspectionCustomStatuses | undefined | null,
  isNoDifferencesExpanded: boolean,
): ListItem[] => {
  const items: ListItem[] = [];

  const statusTitlesById: Record<string, string> = {};
  if (inspectionCustomStatuses?.allStatuses) {
    for (const [id, status] of Object.entries(inspectionCustomStatuses.allStatuses)) {
      statusTitlesById[id] = status.title;
    }
  }
  const allRepeatDifferences = repeatsWithDifferences.flatMap(({ repeat }) => repeat.differencesPostFiltering);
  const numbered = computeStepAndRepeatNumbering(allRepeatDifferences, fromVerifyDifference, {
    sortOptions: { sortBy: sortOption },
    statusTitlesById,
  });
  const numberById = new Map(numbered.map((difference) => [difference.id, difference.groupIndex]));

  // Process repeats with differences first
  repeatsWithDifferences.forEach(({ repeat, sourcePageNumber }) => {
    items.push({
      type: ListItemType.Subheading,
      repeat,
      sourcePageNumber,
    });

    // Order this repeat's differences by the shared sequential number.
    const orderedDifferences = [...repeat.differencesPostFiltering].sort(
      (a, b) => (numberById.get(a.id) ?? 0) - (numberById.get(b.id) ?? 0),
    );
    orderedDifferences.forEach((difference) => {
      items.push({
        type: ListItemType.Difference,
        difference,
        // Sequential number shared with the report, stored 0-based for the card label.
        index: (numberById.get(difference.id) ?? 1) - 1,
      });
    });
  });

  // Process repeats without differences at the bottom
  if (repeatsWithoutDifferences.length > 0) {
    items.push({
      type: ListItemType.CollapsibleHeader,
      count: repeatsWithoutDifferences.length,
    });

    // Only add the repeats if expanded
    if (isNoDifferencesExpanded) {
      repeatsWithoutDifferences.forEach(({ repeat, sourcePageNumber }) => {
        items.push({
          type: ListItemType.Subheading,
          repeat,
          sourcePageNumber,
        });

        items.push({
          type: ListItemType.NoDifferences,
          repeat,
        });
      });
    }
  }

  return items;
};

/**
 * List index to scroll to when a repeat is focused (dropdown or annotation click).
 * Prefers the "No differences found" row when present so the selected card is in view;
 * otherwise the repeat subheading.
 */
export const findRepeatScrollIndex = (listItems: ListItem[], repeatIndex: number): number => {
  const noDiffIndex = listItems.findIndex(
    (item) => item.type === ListItemType.NoDifferences && item.repeat.index === repeatIndex,
  );
  if (noDiffIndex > -1) return noDiffIndex;
  return listItems.findIndex((item) => item.type === ListItemType.Subheading && item.repeat.index === repeatIndex);
};
